import { useCallback, useId, useMemo, useState, type FormEvent } from 'react';

import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { Campo, type OpcaoDeSelecao } from '@/componentes/Campo';
import { Barra, ControleCarregando, EsqueletoDeLista, EsqueletoTexto } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { formatarDataHora, formatarDecimal, formatarInteiro, interpretarNumero } from '@/lib/formato';
import { textoDoRascunho, useRascunho } from '@/lib/rascunho';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';
import { SeloSemAplicacao } from '@/modulos/estoque/SeloSemAplicacao';
import { useSessao } from '@/modulos/sessao/consultas';

import type { EnvioDeTarefa, Gatilho, PlanoDaApi, SituacaoDoPlano, TarefaDaApi } from './api';
import {
  explicarFalha,
  useAcrescentarTarefa,
  useArquivar,
  useCadastrosDaManutencao,
  useCriarPlano,
  useExtrairPlano,
  usePlano,
  usePlanos,
  usePublicar,
  useRevisar,
  useTirarTarefa,
  useAcrescentarItem,
  useTirarItem,
} from './consultas';
import { textoDoIntervalo } from './vencimento';

/**
 * Os planos: montar em rascunho, revisar, publicar, abrir revisão, arquivar.
 *
 * A tela repete a regra que o banco cobra (migração 0018): o plano publicado
 * não se edita. Por isso o painel de um publicado não tem campo nenhum — tem
 * "Abrir revisão", que copia as tarefas para um rascunho. Mostrar um campo que
 * o servidor vai recusar seria prometer o que o sistema não faz.
 */

const ROTULO_DA_SITUACAO: Readonly<Record<SituacaoDoPlano, string>> = {
  rascunho: 'Rascunho',
  publicado: 'Publicado',
  arquivado: 'Arquivado',
};

const ROTULO_DO_ALVO: Readonly<Record<PlanoDaApi['alvoTipo'], string>> = {
  modelo: 'Modelo',
  configuracao: 'Configuração',
  equipamento: 'Máquina',
};

/** A frase do controle que espera as opções do alvo (p.32). */
const CARREGANDO_O_ALVO: Readonly<Record<PlanoDaApi['alvoTipo'], string>> = {
  modelo: 'Carregando os modelos',
  configuracao: 'Carregando as configurações',
  equipamento: 'Carregando as máquinas',
};

const ROTULO_DO_GATILHO: Readonly<Record<Gatilho, string>> = {
  horas: 'Horas de horímetro',
  km: 'Quilômetros',
  dias: 'Dias de calendário',
};

const PADRAO_POR_PAGINA = 10;
const OPCOES_POR_PAGINA = [10, 25, 50, 100];

type Recusa = { readonly erro: string; readonly comoResolver: string } | null;

/** Erro de cada campo, pelo nome dele. `null` = conferido e bom. */
type Erros = Readonly<Record<string, string | null>>;

/** Liga o campo à mensagem abaixo dele (p.16), para o leitor de tela. */
function marcaDeErro(erro: string | null | undefined, id: string) {
  return erro ? { 'aria-invalid': true as const, 'aria-describedby': `${id}-erro` } : {};
}

/** Só a recusa do SERVIDOR mora aqui; o erro de campo vai abaixo do campo. */
function AvisoDeRecusa({ recusa }: { readonly recusa: Recusa }) {
  if (recusa === null) return null;
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
      <span className="txt-corpo text-tinta">{recusa.erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
    </div>
  );
}

/**
 * A lista dos planos. O painel do plano aberto NÃO mora aqui: ele é irmão da
 * área rolável da página (R01, p.28 — fixo ao lado da lista a partir de
 * 1280 px), e quem guarda qual plano está aberto é a página.
 */
export function PlanosDaManutencao({
  aberto,
  aoAbrir,
}: {
  readonly aberto: string | null;
  readonly aoAbrir: (id: string) => void;
}) {
  const planos = usePlanos();
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);

  const todos = planos.data ?? [];
  const paginas = Math.max(Math.ceil(todos.length / porPagina), 1);
  const paginaSegura = Math.min(pagina, paginas);
  const inicio = (paginaSegura - 1) * porPagina;
  const visiveis = todos.slice(inicio, inicio + porPagina);

  const COLUNAS: readonly Coluna<PlanoDaApi>[] = [
    {
      chave: 'plano',
      rotulo: 'Plano',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{p.nome}</span>
          <span className="txt-apoio text-tinta-secundaria">
            {ROTULO_DO_ALVO[p.alvoTipo]}: {p.alvo ?? 'cadastro removido'}
          </span>
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      alinhamento: 'status',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{ROTULO_DA_SITUACAO[p.situacao]}</span>
          {p.substituiId && p.situacao === 'rascunho' ? (
            <span className="txt-apoio text-tinta-secundaria">revisão de um plano publicado</span>
          ) : null}
        </span>
      ),
    },
    {
      chave: 'tarefas',
      rotulo: 'Tarefas',
      alinhamento: 'numero',
      celula: (p) => <span className="numero text-tinta">{formatarInteiro(p.tarefas)}</span>,
    },
    {
      chave: 'publicado',
      rotulo: 'Publicado',
      /* Sai abaixo de 1280 px (R01, p.28); continua nos dados-chave do painel. */
      prioridade: 'secundaria',
      celula: (p) =>
        p.publicadoEm ? (
          <span className="flex flex-col">
            {/* Data com hora não parte (p.12). */}
            <span className="txt-corpo whitespace-nowrap text-tinta">{formatarDataHora(new Date(p.publicadoEm))}</span>
            <span className="txt-apoio text-tinta-secundaria">{p.publicadoPor ?? 'sem nome'}</span>
          </span>
        ) : (
          <span className="txt-apoio text-tinta-secundaria">Ainda não</span>
        ),
    },
  ];

  return (
    <>
      <div className="flex flex-col gap-2">
        <h2 className="txt-secao text-tinta">Planos</h2>
        <p className="txt-apoio text-pretty text-tinta-secundaria">
          O plano publicado não se edita: corrigir é abrir uma revisão dele, e publicar a revisão arquiva o
          anterior. É assim que o histórico de preventivas continua dizendo a verdade.
        </p>
      </div>

      {/* A lista é a origem do painel: clicar em outra linha troca o plano
          aberto em vez de fechar o painel (p.19). */}
      <div data-painel-origem>
        <Tabela
          rotulo="Planos preventivos"
          colunas={COLUNAS}
          linhas={visiveis}
          alturaDupla
          chaveDaLinha={(p) => p.id}
          carregando={planos.isPending}
          selecionada={aberto}
          aoSelecionar={(p) => {
            aoAbrir(p.id);
          }}
          erro={
            planos.isError ? (
              <EstadoVazio
                titulo="Não foi possível carregar os planos"
                descricao={explicarFalha(planos.error).comoResolver}
                acao={
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      void planos.refetch();
                    }}
                  >
                    Carregar de novo
                  </Botao>
                }
              />
            ) : null
          }
          vazio={
            <EstadoVazio
              titulo="Nenhum plano preventivo cadastrado"
              descricao="Monte o primeiro plano a partir do manual do fabricante: as tarefas, o intervalo de cada uma e a página de onde saíram."
            />
          }
          rodape={
            <RodapeTabela
              primeiro={inicio + 1}
              ultimo={inicio + visiveis.length}
              total={todos.length}
              pagina={paginaSegura}
              paginas={paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={(novo) => {
                setPorPagina(novo);
                setPagina(1);
              }}
            />
          }
        />
      </div>
    </>
  );
}

/* ========================================================================
 * O PAINEL DO PLANO
 * ===================================================================== */

export function PainelDoPlano({
  id,
  podeGerir,
  podePublicar,
  aoAbrir,
  aoFechar,
}: {
  readonly id: string;
  readonly podeGerir: boolean;
  readonly podePublicar: boolean;
  readonly aoAbrir: (id: string) => void;
  readonly aoFechar: () => void;
}) {
  const consulta = usePlano(id);
  /* A linha clicada já diz o nome, o alvo e a situação: a lista e este painel
     leem a mesma consulta, e o cabeçalho não espera o detalhe (p.32). */
  const daLista = usePlanos().data?.find((p) => p.id === id) ?? null;
  const revisar = useRevisar();
  const [recusa, setRecusa] = useState<Recusa>(null);

  const plano = consulta.data?.plano;
  const tarefas = consulta.data?.tarefas ?? [];

  if (!plano && consulta.isPending) {
    /* Carregando (p.32): o que já se sabe fica — a identidade da linha, o
       rótulo dos dados-chave e o nome das abas —, e só o dado vira barra. */
    return (
      <PainelDetalhe
        titulo={daLista?.nome ?? 'Plano'}
        subtitulo={daLista ? `${ROTULO_DO_ALVO[daLista.alvoTipo]}: ${daLista.alvo ?? 'cadastro removido'}` : ''}
        {...(daLista
          ? { marcadores: <span className="txt-corpo text-tinta">{ROTULO_DA_SITUACAO[daLista.situacao]}</span> }
          : {})}
        dadosChave={
          <>
            <DadoChave rotulo="Documento de origem">
              <Barra className="my-1 h-3 w-3/4" />
            </DadoChave>
            <DadoChave rotulo="Publicado">
              <Barra className="my-1 h-3 w-1/2" />
            </DadoChave>
          </>
        }
        abas={[
          {
            id: 'tarefas',
            rotulo: 'Tarefas',
            conteudo: <EsqueletoDeLista oQue="Carregando as tarefas do plano" itens={3} icone={false} />,
          },
          {
            id: 'revisao',
            rotulo: 'Revisão',
            conteudo: <EsqueletoTexto oQue="Carregando a revisão do plano" linhas={2} />,
          },
        ]}
        aoFechar={aoFechar}
      />
    );
  }

  if (!plano) {
    return (
      <PainelDetalhe
        titulo="Plano"
        subtitulo={consulta.isError ? explicarFalha(consulta.error).erro : 'Plano não encontrado'}
        dadosChave={null}
        abas={[
          {
            id: 'tarefas',
            rotulo: 'Tarefas',
            conteudo: (
              <p className="txt-apoio text-tinta-secundaria">
                {consulta.isError ? explicarFalha(consulta.error).comoResolver : 'Feche o painel e abra o plano de novo pela lista.'}
              </p>
            ),
          },
        ]}
        aoFechar={aoFechar}
      />
    );
  }

  const rascunho = plano.situacao === 'rascunho';
  const acoes =
    plano.situacao === 'publicado' && podeGerir
      ? [
          plano.revisaoAbertaId ? (
            <Botao
              key="continuar"
              nivel="secundario"
              onClick={() => {
                aoAbrir(plano.revisaoAbertaId ?? plano.id);
              }}
            >
              Continuar revisão
            </Botao>
          ) : (
            <Botao
              key="revisar"
              nivel="secundario"
              disabled={revisar.isPending}
              onClick={() => {
                setRecusa(null);
                revisar.mutate(plano.id, {
                  onSuccess: (nova) => {
                    aoAbrir(nova.id);
                  },
                  onError: (falha) => {
                    setRecusa(explicarFalha(falha));
                  },
                });
              }}
            >
              Abrir revisão
            </Botao>
          ),
        ]
      : [];

  return (
    <PainelDetalhe
      titulo={plano.nome}
      subtitulo={`${ROTULO_DO_ALVO[plano.alvoTipo]}: ${plano.alvo ?? 'cadastro removido'}`}
      marcadores={<span className="txt-corpo text-tinta">{ROTULO_DA_SITUACAO[plano.situacao]}</span>}
      dadosChave={
        <>
          <DadoChave rotulo="Documento de origem">{plano.documento ?? 'Sem documento vinculado'}</DadoChave>
          <DadoChave rotulo="Publicado">
            {plano.publicadoEm
              ? `${formatarDataHora(new Date(plano.publicadoEm))} · ${plano.publicadoPor ?? 'sem nome'}`
              : 'Ainda não'}
          </DadoChave>
        </>
      }
      abas={[
        {
          id: 'tarefas',
          rotulo: 'Tarefas',
          conteudo: (
            <div className="flex flex-col gap-4">
              <ListaDeTarefas tarefas={tarefas} podeTirar={rascunho && podeGerir} />
              {rascunho && podeGerir && tarefas.length > 0 ? <NovoItem tarefas={tarefas} /> : null}
              {rascunho && podeGerir ? <NovaTarefa planoId={plano.id} /> : null}
              {!rascunho ? (
                <p className="txt-apoio text-tinta-secundaria">
                  {plano.situacao === 'publicado'
                    ? 'As tarefas de um plano publicado não mudam. Para corrigir, abra uma revisão.'
                    : 'Plano arquivado: fica no histórico e não vale mais para nenhuma máquina.'}
                </p>
              ) : null}
            </div>
          ),
        },
        {
          id: 'revisao',
          rotulo: 'Revisão',
          conteudo: (
            <div className="flex flex-col gap-4">
              {plano.revisaoNota ? (
                <div className="flex flex-col gap-1">
                  <span className="txt-rotulo text-tinta-secundaria">Conferido contra</span>
                  <span className="txt-corpo text-tinta">{plano.revisaoNota}</span>
                </div>
              ) : null}
              {rascunho && podePublicar ? (
                <Publicacao planoId={plano.id} substitui={plano.substituiId !== null} />
              ) : rascunho ? (
                <p className="txt-apoio text-tinta-secundaria">
                  Publicar é a revisão técnica, e o seu perfil não tem essa permissão. Peça ao responsável técnico
                  para revisar e publicar este plano.
                </p>
              ) : null}
              {plano.situacao !== 'arquivado' && podeGerir ? <Arquivamento planoId={plano.id} nome={plano.nome} /> : null}
            </div>
          ),
        },
      ]}
      acoes={acoes}
      {...(recusa ? { notaDasAcoes: `${recusa.erro}. ${recusa.comoResolver}` } : {})}
      aoFechar={aoFechar}
    />
  );
}

function ListaDeTarefas({ tarefas, podeTirar }: { readonly tarefas: readonly TarefaDaApi[]; readonly podeTirar: boolean }) {
  const tirar = useTirarTarefa();
  if (tarefas.length === 0) {
    return (
      <p className="txt-apoio text-tinta-secundaria">
        Nenhuma tarefa ainda. Um plano sem tarefa não se publica.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {tarefas.map((t) => (
        <li key={t.id} className="flex items-start justify-between gap-4 border-b border-borda-sutil pb-2">
          <span className="flex flex-col">
            <span className="txt-corpo text-tinta">{t.descricao}</span>
            <span className="txt-apoio text-tinta-secundaria">
              {[textoDoIntervalo(t.gatilho, t.intervalo), t.pagina ? `página ${String(t.pagina)}` : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
            {/* A LISTA TÉCNICA (ferramenta 3 do 05): o que a tarefa consome. */}
            {t.itens.length === 0 ? (
              <span className="txt-apoio text-tinta-apagada">Sem lista técnica</span>
            ) : (
              t.itens.map((i) => (
                <span key={i.id} className="flex flex-wrap items-center gap-2 txt-apoio text-tinta-secundaria">
                  {/* Número e unidade separados (p.12): o número no tom do
                      texto, com dígitos de largura fixa; a unidade em cinza.
                      No tamanho da linha, 12 px. */}
                  <span>
                    {`${i.produto} · `}
                    <span className="whitespace-nowrap">
                      <span className="tabular-nums text-tinta">{formatarDecimal(i.quantidade, 1)}</span>
                      {` ${i.unidade}`}
                    </span>
                  </span>
                  {i.semAplicacaoValidada ? <SeloSemAplicacao /> : null}
                  {podeTirar ? <TirarItem id={i.id} /> : null}
                </span>
              ))
            )}
          </span>
          {podeTirar ? (
            <Botao
              nivel="terciario"
              disabled={tirar.isPending}
              onClick={() => {
                tirar.mutate(t.id);
              }}
            >
              Tirar tarefa
            </Botao>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function TirarItem({ id }: { readonly id: string }) {
  const tirar = useTirarItem();
  return (
    <Botao
      nivel="terciario"
      disabled={tirar.isPending}
      onClick={() => {
        tirar.mutate(id);
      }}
    >
      Tirar item
    </Botao>
  );
}

/** Acrescentar à lista técnica: que item do estoque a tarefa consome, e quanto. */
function NovoItem({ tarefas }: { readonly tarefas: readonly TarefaDaApi[] }) {
  const base = useId();
  const cadastros = useCadastrosDaManutencao();
  const acrescentar = useAcrescentarItem();
  /* A TAREFA MAIS NOVA já vem escolhida (31/10/2026): quem monta a lista
     técnica acrescenta os itens da tarefa que acabou de criar. O campo fica,
     e com uma tarefa só ele vem fixo (opção única). */
  const [tarefa, setTarefa] = useState(tarefas[tarefas.length - 1]?.id ?? '');
  const [produto, setProduto] = useState('');
  const [quantidade, setQuantidade] = useState('1');
  const [erros, setErros] = useState<Erros>({});
  const [recusa, setRecusa] = useState<Recusa>(null);
  const unidadeDoItem = (cadastros.data?.produtos ?? []).find((p) => p.id === produto)?.unidade ?? 'un';

  function checar(campo: 'tarefa' | 'produto' | 'quantidade'): string | null {
    if (campo === 'tarefa') return tarefa === '' ? 'Escolha a tarefa que consome o item.' : null;
    if (campo === 'produto') return produto === '' ? 'Escolha o item do estoque que a tarefa consome.' : null;
    const q = interpretarNumero(quantidade);
    return q === null || q <= 0 ? 'Informe a quantidade, maior que zero.' : null;
  }

  function sair(campo: 'tarefa' | 'produto' | 'quantidade') {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = { tarefa: checar('tarefa'), produto: checar('produto'), quantidade: checar('quantidade') };
    setErros(achados);
    const q = interpretarNumero(quantidade);
    if (achados.tarefa || achados.produto || achados.quantidade || q === null) return;
    setRecusa(null);
    acrescentar.mutate(
      { tarefaId: tarefa, produtoId: produto, quantidade: q },
      { onSuccess: () => { setProduto(''); setQuantidade('1'); }, onError: (falha) => { setRecusa(explicarFalha(falha)); } },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
      <span className="txt-rotulo text-tinta-secundaria">Lista técnica</span>
      <div className="grade-do-painel">
        <Campo id={`${base}-tarefa`} rotulo="Tarefa" erro={erros['tarefa'] ?? null}>
          <ListaDeEscolha
            id={`${base}-tarefa`}
            rotulo="Tarefa"
            valor={tarefa}
            aoMudar={(v) => { setTarefa(v); setErros((a) => ({ ...a, tarefa: null })); }}
            aoSair={() => { sair('tarefa'); }}
            opcoes={tarefas.map((t) => ({ valor: t.id, rotulo: t.descricao, detalhe: textoDoIntervalo(t.gatilho, t.intervalo) }))}
            idDoErro={erros['tarefa'] ? `${base}-tarefa-erro` : undefined}
          />
        </Campo>
        <Campo id={`${base}-item`} rotulo="Item do estoque" erro={erros['produto'] ?? null}>
          {/* Os itens ainda vêm (p.32): a barra na largura do campo, e não
              uma lista só com "Escolha o item". */}
          {cadastros.isPending ? (
            <ControleCarregando oQue="Carregando os itens do estoque" />
          ) : (
            /* O seletor do sistema, com busca (31/10/2026). */
            <ListaDeEscolha
              id={`${base}-item`}
              rotulo="Item do estoque"
              valor={produto}
              aoMudar={(v) => { setProduto(v); setErros((a) => ({ ...a, produto: null })); }}
              aoSair={() => { sair('produto'); }}
              rotuloVazio="Escolha o item"
              opcoes={(cadastros.data?.produtos ?? []).map((p) => ({ valor: p.id, rotulo: p.codigo, detalhe: p.nome }))}
              idDoErro={erros['produto'] ? `${base}-item-erro` : undefined}
            />
          )}
        </Campo>
        <Campo id={`${base}-qtd`} rotulo="Quantidade" erro={erros['quantidade'] ?? null}>
          <div className="flex items-center gap-2">
            <input
              id={`${base}-qtd`}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="controle w-full"
              value={quantidade}
              onChange={(e) => { setQuantidade(e.target.value); setErros((a) => ({ ...a, quantidade: null })); }}
              onBlur={() => { sair('quantidade'); }}
              {...marcaDeErro(erros['quantidade'], `${base}-qtd`)}
            />
            <span className="txt-corpo shrink-0 text-tinta-apagada">{unidadeDoItem}</span>
          </div>
        </Campo>
      </div>
      <div className="flex justify-end">
        <Botao nivel="secundario" type="submit" disabled={acrescentar.isPending}>
          Acrescentar item
        </Botao>
      </div>
      <AvisoDeRecusa recusa={recusa} />
    </form>
  );
}

/* ------------------------------------------------------------ a tarefa */

interface RascunhoDeTarefa {
  descricao: string;
  gatilho: Gatilho;
  intervalo: string;
  aviso: string;
  pagina: string;
}

type CampoDaTarefa = keyof RascunhoDeTarefa;
type ErrosDaTarefa = Readonly<Partial<Record<CampoDaTarefa, string | null>>>;

const TAREFA_VAZIA: RascunhoDeTarefa = { descricao: '', gatilho: 'horas', intervalo: '', aviso: '', pagina: '' };

/** Tarefa que a pessoa deixou em branco não entra no plano, nem é cobrada. */
function tarefaEmBranco(t: RascunhoDeTarefa): boolean {
  return t.descricao.trim() === '' && t.intervalo.trim() === '';
}

/**
 * O que falta em cada campo da tarefa, com a frase que diz o que fazer. As
 * regras são as mesmas de sempre; o que mudou é ONDE a frase aparece: abaixo
 * do campo que ela descreve (p.16), e não numa linha solta no fim da lista.
 */
function errosDaTarefa(t: RascunhoDeTarefa): ErrosDaTarefa {
  const erros: Partial<Record<CampoDaTarefa, string>> = {};
  if (t.descricao.trim() === '') erros.descricao = 'Descreva a tarefa.';
  const intervalo = interpretarNumero(t.intervalo);
  if (intervalo === null || intervalo <= 0) erros.intervalo = 'Informe o intervalo, maior que zero.';
  else if (t.gatilho === 'dias' && !Number.isInteger(intervalo)) erros.intervalo = 'O intervalo em dias é um número inteiro.';
  else {
    const aviso = t.aviso.trim() === '' ? null : interpretarNumero(t.aviso);
    if (aviso !== null && aviso >= intervalo) {
      erros.aviso = 'O aviso tem de chegar antes do vencimento: use um valor menor que o intervalo.';
    }
  }
  return erros;
}

function temErro(erros: ErrosDaTarefa | Erros): boolean {
  return Object.values(erros).some(Boolean);
}

/** Do que foi digitado para o que a API aceita — ou a frase do que falta. */
function lerTarefa(t: RascunhoDeTarefa): { envio: EnvioDeTarefa } | { erro: string } {
  const primeiro = Object.values(errosDaTarefa(t)).find((m): m is string => Boolean(m));
  if (primeiro) return { erro: primeiro };
  const intervalo = interpretarNumero(t.intervalo);
  if (intervalo === null) return { erro: 'Informe o intervalo, maior que zero.' };
  const aviso = t.aviso.trim() === '' ? null : interpretarNumero(t.aviso);
  const pagina = t.pagina.trim() === '' ? null : interpretarNumero(t.pagina);
  return {
    envio: {
      descricao: t.descricao.trim(),
      gatilho: t.gatilho,
      intervalo,
      avisoAntes: aviso,
      pagina: pagina === null ? null : Math.trunc(pagina),
    },
  };
}

function CamposDaTarefa({
  base,
  valor,
  aoMudar,
  erros = {},
  aoSair,
  emGrade = false,
}: {
  readonly base: string;
  readonly valor: RascunhoDeTarefa;
  readonly aoMudar: (v: RascunhoDeTarefa, campo: CampoDaTarefa) => void;
  readonly erros?: ErrosDaTarefa;
  /** Valida AO SAIR do campo (p.16), nunca a cada tecla. */
  readonly aoSair?: (campo: CampoDaTarefa) => void;
  /** Na página, a grade da R01. No painel de detalhe (360-400 px), coluna
      única: a `formulario-grade` mede a tela, não o painel. */
  readonly emGrade?: boolean;
}) {
  function mudar(campo: Exclude<CampoDaTarefa, 'gatilho'>, texto: string) {
    aoMudar({ ...valor, [campo]: texto }, campo);
  }

  return (
    /* R01 (p.28): grade, largura pelo dado — a descrição em duas colunas. */
    <div className={emGrade ? 'formulario-grade' : 'flex flex-col gap-4'}>
      <div className="campo-largo">
        <Campo id={`${base}-descricao`} rotulo="Tarefa" erro={erros.descricao ?? null}>
          <input
            id={`${base}-descricao`}
            type="text"
            autoComplete="off"
            className="controle w-full"
            placeholder="Troca do óleo do motor"
            value={valor.descricao}
            onChange={(e) => {
              mudar('descricao', e.target.value);
            }}
            onBlur={() => aoSair?.('descricao')}
            {...marcaDeErro(erros.descricao, `${base}-descricao`)}
          />
        </Campo>
      </div>
      <Campo id={`${base}-gatilho`} rotulo="Vence por">
        <ListaDeEscolha
          id={`${base}-gatilho`}
          rotulo="Vence por"
          valor={valor.gatilho}
          aoMudar={(v) => {
            aoMudar({ ...valor, gatilho: v as Gatilho }, 'gatilho');
          }}
          opcoes={(Object.keys(ROTULO_DO_GATILHO) as Gatilho[]).map((g) => ({ valor: g, rotulo: ROTULO_DO_GATILHO[g] }))}
        />
      </Campo>
      <HistoriaNaMesa codigo="CMP-MNT-04" className="min-w-0">
        <Campo id={`${base}-intervalo`} rotulo="Intervalo" erro={erros.intervalo ?? null}>
          <input
            id={`${base}-intervalo`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="controle w-full"
            placeholder={valor.gatilho === 'dias' ? '365' : '250'}
            value={valor.intervalo}
            onChange={(e) => {
              mudar('intervalo', e.target.value);
            }}
            onBlur={() => aoSair?.('intervalo')}
            {...marcaDeErro(erros.intervalo, `${base}-intervalo`)}
          />
        </Campo>
      </HistoriaNaMesa>
      <Campo id={`${base}-aviso`} rotulo="Avisar antes" opcional ajuda="Vazio: 10% do intervalo." erro={erros.aviso ?? null}>
        <input
          id={`${base}-aviso`}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className="controle w-full"
          value={valor.aviso}
          onChange={(e) => {
            mudar('aviso', e.target.value);
          }}
          onBlur={() => aoSair?.('aviso')}
          {...marcaDeErro(erros.aviso, `${base}-aviso`)}
        />
      </Campo>
      <HistoriaNaMesa codigo="NUM-MNT-05" className="min-w-0">
        <Campo id={`${base}-pagina`} rotulo="Página do manual" opcional>
          <input
            id={`${base}-pagina`}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            className="controle w-full"
            value={valor.pagina}
            onChange={(e) => {
              mudar('pagina', e.target.value);
            }}
          />
        </Campo>
      </HistoriaNaMesa>
    </div>
  );
}

function NovaTarefa({ planoId }: { readonly planoId: string }) {
  const base = useId();
  const acrescentar = useAcrescentarTarefa();
  const [tarefa, setTarefa] = useState<RascunhoDeTarefa>(TAREFA_VAZIA);
  const [erros, setErros] = useState<ErrosDaTarefa>({});
  const [recusa, setRecusa] = useState<Recusa>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = errosDaTarefa(tarefa);
    setErros(achados);
    const lida = lerTarefa(tarefa);
    if (temErro(achados) || 'erro' in lida) return;
    setRecusa(null);
    acrescentar.mutate(
      { planoId, envio: lida.envio },
      {
        onSuccess: () => {
          setTarefa(TAREFA_VAZIA);
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
      <CamposDaTarefa
        base={base}
        valor={tarefa}
        erros={erros}
        aoMudar={(nova, campo) => {
          setTarefa(nova);
          setErros((atual) => ({ ...atual, [campo]: null }));
        }}
        aoSair={(campo) => {
          setErros((atual) => ({ ...atual, [campo]: errosDaTarefa(tarefa)[campo] ?? null }));
        }}
      />
      <AvisoDeRecusa recusa={recusa} />
      <div>
        <Botao nivel="secundario" type="submit" disabled={acrescentar.isPending}>
          Acrescentar tarefa
        </Botao>
      </div>
    </form>
  );
}

/* ------------------------------------------------- publicar e arquivar */

function Publicacao({ planoId, substitui }: { readonly planoId: string; readonly substitui: boolean }) {
  const base = useId();
  const publicar = usePublicar();
  const [nota, setNota] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<Recusa>(null);

  const FALTA_A_NOTA = 'Escreva contra o que o plano foi conferido: o manual, a revisão e a página, ou o procedimento da empresa.';

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (nota.trim() === '') {
      setErro(FALTA_A_NOTA);
      return;
    }
    setErro(null);
    setRecusa(null);
    publicar.mutate(
      { planoId, nota: nota.trim() },
      {
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
      <Campo
        id={`${base}-nota`}
        rotulo="Conferido contra"
        erro={erro}
        ajuda={
          substitui
            ? 'Publicar esta revisão arquiva o plano que ela substitui. As realizações registradas continuam valendo.'
            : 'A partir da publicação, o plano vale para toda máquina que ele alcança.'
        }
      >
        <input
          id={`${base}-nota`}
          type="text"
          autoComplete="off"
          className="controle w-full"
          placeholder="Manual do fabricante, rev. C, p. 112"
          value={nota}
          onChange={(e) => {
            setNota(e.target.value);
            setErro(null);
          }}
          onBlur={() => {
            setErro(nota.trim() === '' ? FALTA_A_NOTA : null);
          }}
          {...marcaDeErro(erro, `${base}-nota`)}
        />
      </Campo>
      <AvisoDeRecusa recusa={recusa} />
      <div>
        {/* SECUNDÁRIO de propósito: o laranja da tela é de "Montar plano". */}
        <Botao nivel="secundario" type="submit" disabled={publicar.isPending}>
          Publicar plano
        </Botao>
      </div>
    </form>
  );
}

/**
 * Arquivar não volta (migração 0018). Por isso o botão é o destrutivo com
 * confirmação (p.15): a pergunta nomeia o plano e a consequência, no formato
 * do manual (p.23), e o motivo escrito no campo vai junto na confirmação.
 */
function Arquivamento({ planoId, nome }: { readonly planoId: string; readonly nome: string }) {
  const base = useId();
  const arquivar = useArquivar();
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<Recusa>(null);

  const FALTA_O_MOTIVO = 'Escreva por que o plano sai de uso.';
  const escrito = motivo.trim();

  return (
    <div className="flex flex-col gap-4 border-t border-borda-sutil pt-4">
      <Campo
        id={`${base}-motivo`}
        rotulo="Arquivar com o motivo"
        erro={erro}
        ajuda="Arquivado não volta e deixa de valer para as máquinas. Fica no histórico."
      >
        <input
          id={`${base}-motivo`}
          type="text"
          autoComplete="off"
          className="controle w-full"
          placeholder="Modelo saiu da frota"
          value={motivo}
          onChange={(e) => {
            setMotivo(e.target.value);
            setErro(null);
          }}
          onBlur={() => {
            setErro(motivo.trim() === '' ? FALTA_O_MOTIVO : null);
          }}
          {...marcaDeErro(erro, `${base}-motivo`)}
        />
      </Campo>
      <AvisoDeRecusa recusa={recusa} />
      <div>
        <BotaoDestrutivo
          rotulo="Arquivar plano"
          pergunta={`Arquivar o plano ${nome}?`}
          consequencia={
            escrito === ''
              ? 'O plano deixa de valer para as máquinas que ele alcança e fica só no histórico. Arquivado não volta.'
              : `O plano deixa de valer para as máquinas que ele alcança e fica no histórico com o motivo “${escrito}”. Arquivado não volta.`
          }
          rotuloConfirmar="Arquivar plano"
          {...(escrito === ''
            ? { motivoIndisponivel: 'Falta o motivo. Volte, escreva no campo por que o plano sai de uso e arquive outra vez.' }
            : {})}
          aoConfirmar={() => {
            setRecusa(null);
            arquivar.mutate(
              { planoId, motivo: escrito },
              {
                onError: (falha) => {
                  setRecusa(explicarFalha(falha));
                },
              },
            );
          }}
        />
      </div>
    </div>
  );
}

/* ========================================================================
 * MONTAR PLANO
 * ===================================================================== */

/**
 * O rascunho automático (p.16: "formulário com mais de dez campos salva
 * rascunho automático. Conexão em obra cai, e o preenchimento não pode se
 * perder"). Com duas tarefas o formulário já passa de dez campos. Quem guarda
 * é o `useRascunho` comum, no navegador, por empresa e pessoa.
 *
 * Não é o plano em rascunho do SERVIDOR — aquele nasce do botão "Gravar
 * rascunho". Este é a cópia de segurança de quem está digitando.
 */
interface PreenchimentoDoPlano {
  readonly nome: string;
  readonly alvoTipo: PlanoDaApi['alvoTipo'];
  readonly alvoId: string;
  readonly documentoId: string;
  readonly tarefas: readonly RascunhoDeTarefa[];
}

function ehTarefaGuardada(v: unknown): v is RascunhoDeTarefa {
  if (typeof v !== 'object' || v === null) return false;
  const t = v as Record<string, unknown>;
  return (
    typeof t['descricao'] === 'string' &&
    (t['gatilho'] === 'horas' || t['gatilho'] === 'km' || t['gatilho'] === 'dias') &&
    typeof t['intervalo'] === 'string' &&
    typeof t['aviso'] === 'string' &&
    typeof t['pagina'] === 'string'
  );
}

/* O que volta do navegador é entrada: forma errada é descartada, nunca
   "consertada" no chute. */
function ehPreenchimento(v: unknown): v is PreenchimentoDoPlano {
  if (typeof v !== 'object' || v === null) return false;
  const p = v as Record<string, unknown>;
  const tarefas = p['tarefas'];
  return (
    typeof p['nome'] === 'string' &&
    (p['alvoTipo'] === 'modelo' || p['alvoTipo'] === 'configuracao' || p['alvoTipo'] === 'equipamento') &&
    typeof p['alvoId'] === 'string' &&
    typeof p['documentoId'] === 'string' &&
    Array.isArray(tarefas) &&
    tarefas.length > 0 &&
    tarefas.every(ehTarefaGuardada)
  );
}

export function FormularioDePlano({ aoFechar }: { readonly aoFechar: () => void }) {
  const base = useId();
  const cadastros = useCadastrosDaManutencao();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const criar = useCriarPlano();
  const extrair = useExtrairPlano();
  const sessao = useSessao();
  const podeExtrair = (sessao.data?.permissoes ?? []).includes('ia.perguntar');
  const [extracao, setExtracao] = useState<string | null>(null);

  const [nome, setNome] = useState('');
  const [alvoTipo, setAlvoTipo] = useState<PlanoDaApi['alvoTipo']>('modelo');
  const [alvoId, setAlvoId] = useState('');
  const [documentoId, setDocumentoId] = useState('');
  const [tarefas, setTarefas] = useState<readonly RascunhoDeTarefa[]>([TAREFA_VAZIA]);
  const [erros, setErros] = useState<Erros>({});
  const [errosDasTarefas, setErrosDasTarefas] = useState<readonly ErrosDaTarefa[]>([]);
  const [recusa, setRecusa] = useState<Recusa>(null);

  /* A chave leva a empresa e a pessoa: o rascunho de uma não aparece na outra. */
  const preenchimento = useMemo<PreenchimentoDoPlano>(
    () => ({ nome, alvoTipo, alvoId, documentoId, tarefas }),
    [nome, alvoTipo, alvoId, documentoId, tarefas],
  );
  const devolver = useCallback((guardado: PreenchimentoDoPlano) => {
    if (!ehPreenchimento(guardado)) return;
    setNome(guardado.nome);
    setAlvoTipo(guardado.alvoTipo);
    setAlvoId(guardado.alvoId);
    setDocumentoId(guardado.documentoId);
    setTarefas(guardado.tarefas);
  }, []);
  const rascunho = useRascunho(
    `montar-plano:${sessao.data?.empresa ?? ''}:${sessao.data?.usuarioId ?? ''}`,
    preenchimento,
    devolver,
  );

  /* A máquina com o código interno e a placa em etiqueta (31/10/2026). */
  const opcoesDoAlvo: readonly OpcaoDeSelecao[] =
    alvoTipo === 'modelo'
      ? (cadastros.data?.modelos ?? []).map((m) => ({ valor: m.id, rotulo: m.nome }))
      : alvoTipo === 'configuracao'
        ? (cadastros.data?.configuracoes ?? []).map((c) => ({ valor: c.id, rotulo: `${c.modelo} · ${c.nome}` }))
        : comVisualDaFrota((cadastros.data?.equipamentos ?? []).map((e) => opcaoDaMaquina(e)));

  function checar(campo: 'nome' | 'alvo'): string | null {
    if (campo === 'nome') return nome.trim() === '' ? 'Dê um nome ao plano.' : null;
    return alvoId === '' ? 'Escolha para que o plano vale.' : null;
  }

  function sair(campo: 'nome' | 'alvo') {
    setErros((atual) => ({ ...atual, [campo]: checar(campo) }));
  }

  function erroDaTarefa(indice: number, campo: CampoDaTarefa, valor: string | null) {
    setErrosDasTarefas((atual) => {
      const novo = [...atual];
      novo[indice] = { ...(novo[indice] ?? {}), [campo]: valor };
      return novo;
    });
  }

  function comecarDoZero() {
    rascunho.descartar();
    setNome('');
    setAlvoTipo('modelo');
    setAlvoId('');
    setDocumentoId('');
    setTarefas([TAREFA_VAZIA]);
    setErros({});
    setErrosDasTarefas([]);
  }

  function descartar() {
    rascunho.descartar();
    aoFechar();
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = { nome: checar('nome'), alvo: checar('alvo') };
    const porTarefa = tarefas.map((t) => (tarefaEmBranco(t) ? {} : errosDaTarefa(t)));
    setErros(achados);
    setErrosDasTarefas(porTarefa);
    if (temErro(achados) || porTarefa.some(temErro)) return;
    const lidas = tarefas.filter((t) => !tarefaEmBranco(t)).map(lerTarefa);
    setRecusa(null);
    criar.mutate(
      {
        nome: nome.trim(),
        alvo: { tipo: alvoTipo, id: alvoId },
        documentoId: documentoId === '' ? null : documentoId,
        tarefas: lidas.flatMap((l) => ('envio' in l ? [l.envio] : [])),
      },
      {
        onSuccess: () => {
          rascunho.descartar();
          aoFechar();
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <form onSubmit={enviar} noValidate className="cartao flex flex-col">
      {rascunho.recuperadoEm ? (
        <div role="status" className="flex flex-wrap items-center gap-2 border-b border-borda-sutil px-6 py-3">
          <span className="txt-apoio text-tinta-secundaria">{textoDoRascunho(rascunho.recuperadoEm)}</span>
          <Botao nivel="terciario" type="button" onClick={comecarDoZero}>
            Começar do zero
          </Botao>
        </div>
      ) : null}

      {/* R01 (p.28): campos agrupados em seções com título. */}
      <section className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <h2 className="txt-subtitulo text-tinta">Identificação</h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            O plano nasce em rascunho e só passa a valer depois de publicado, com a nota de contra o que foi
            conferido. Até lá, as tarefas se acrescentam e se tiram à vontade.
          </p>
        </div>

        <div className="formulario-grade">
          <div className="campo-largo">
            <Campo id={`${base}-nome`} rotulo="Nome do plano" erro={erros['nome'] ?? null}>
              <input
                id={`${base}-nome`}
                type="text"
                autoComplete="off"
                className="controle w-full"
                placeholder="Preventiva 250 h da escavadeira 320"
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                  setErros((atual) => ({ ...atual, nome: null }));
                }}
                onBlur={() => {
                  sair('nome');
                }}
                {...marcaDeErro(erros['nome'], `${base}-nome`)}
              />
            </Campo>
          </div>
          <Campo id={`${base}-alvo-tipo`} rotulo="Vale para">
            <ListaDeEscolha
              id={`${base}-alvo-tipo`}
              rotulo="Vale para"
              valor={alvoTipo}
              aoMudar={(v) => {
                setAlvoTipo(v as PlanoDaApi['alvoTipo']);
                setAlvoId('');
              }}
              opcoes={[
                { valor: 'modelo', rotulo: 'Um modelo inteiro' },
                { valor: 'configuracao', rotulo: 'Uma configuração' },
                { valor: 'equipamento', rotulo: 'Uma máquina só' },
              ]}
            />
          </Campo>
          <Campo id={`${base}-alvo`} rotulo={ROTULO_DO_ALVO[alvoTipo]} erro={erros['alvo'] ?? null}>
            {/* As opções ainda vêm (p.32): a lista só com "Escolha na lista"
                deixava escolher o nada, e escondia o alvo do rascunho. */}
            {cadastros.isPending ? (
              <ControleCarregando oQue={CARREGANDO_O_ALVO[alvoTipo]} />
            ) : (
              <ListaDeEscolha
                id={`${base}-alvo`}
                rotulo={ROTULO_DO_ALVO[alvoTipo]}
                valor={alvoId}
                aoMudar={(valor) => {
                  setAlvoId(valor);
                  setErros((atual) => ({ ...atual, alvo: null }));
                }}
                aoSair={() => {
                  sair('alvo');
                }}
                rotuloVazio="Escolha na lista"
                opcoes={opcoesDoAlvo}
                idDoErro={erros['alvo'] ? `${base}-alvo-erro` : undefined}
                /* O leitor só quando o plano é de uma máquina: modelo e configuração não têm adesivo. */
                adesivo={alvoTipo === 'equipamento'}
              />
            )}
          </Campo>
          <Campo id={`${base}-documento`} rotulo="Documento de origem" opcional>
            {cadastros.isPending ? (
              <ControleCarregando oQue="Carregando os documentos" />
            ) : (
              /* O seletor do sistema, com busca: a biblioteca passa de sete documentos. */
              <ListaDeEscolha
                id={`${base}-documento`}
                rotulo="Documento de origem"
                valor={documentoId}
                aoMudar={setDocumentoId}
                rotuloVazio="Sem documento"
                opcoes={(cadastros.data?.documentos ?? []).map((d) => ({ valor: d.id, rotulo: d.titulo }))}
              />
            )}
          </Campo>
        </div>
      </section>

      <section className="flex flex-col gap-4 border-t border-borda-sutil p-6">
        <h2 className="txt-subtitulo text-tinta">Tarefas</h2>
        {tarefas.map((t, indice) => (
          <div key={indice} className="flex flex-col gap-2">
            <CamposDaTarefa
              emGrade
              base={`${base}-t${String(indice)}`}
              valor={t}
              erros={errosDasTarefas[indice] ?? {}}
              aoMudar={(nova, campo) => {
                setTarefas(tarefas.map((x, i) => (i === indice ? nova : x)));
                erroDaTarefa(indice, campo, null);
              }}
              aoSair={(campo) => {
                erroDaTarefa(indice, campo, tarefaEmBranco(t) ? null : (errosDaTarefa(t)[campo] ?? null));
              }}
            />
            {tarefas.length > 1 ? (
              <div>
                <Botao
                  nivel="terciario"
                  type="button"
                  onClick={() => {
                    setTarefas(tarefas.filter((_, i) => i !== indice));
                    setErrosDasTarefas(errosDasTarefas.filter((_, i) => i !== indice));
                  }}
                >
                  Tirar tarefa
                </Botao>
              </div>
            ) : null}
          </div>
        ))}
        <div>
          <Botao
            nivel="terciario"
            type="button"
            onClick={() => {
              setTarefas([...tarefas, TAREFA_VAZIA]);
            }}
          >
            Acrescentar tarefa
          </Botao>
        </div>

        {podeExtrair ? (
          <div className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Consulta à IA: azul de consulta (R02, p.31). O azul já diz
                  que é a IA; o rótulo fica em verbo + objeto (p.15). */}
              <Botao
                nivel="consulta"
                type="button"
                disabled={extrair.isPending || documentoId === '' || alvoId === ''}
                onClick={() => {
                  setExtracao(null);
                  setRecusa(null);
                  extrair.mutate(
                    { documentoId, alvo: { tipo: alvoTipo, id: alvoId }, ...(nome.trim() ? { nome: nome.trim() } : {}) },
                    {
                      onSuccess: (r) => {
                        if (r.criado) {
                          rascunho.descartar();
                          aoFechar();
                        } else {
                          const descartadas =
                            r.descartadas === 1
                              ? '1 proposta descartada'
                              : `${formatarInteiro(r.descartadas)} propostas descartadas`;
                          setExtracao(
                            `Nenhuma tarefa aproveitada: ${r.falta ?? 'o manual não traz intervalo nos trechos lidos'}. ${descartadas} na conferência.`,
                          );
                        }
                      },
                      onError: (falha) => {
                        setRecusa(explicarFalha(falha));
                      },
                    },
                  );
                }}
              >
                {extrair.isPending ? 'Lendo o manual…' : 'Extrair tarefas'}
              </Botao>
              <span className="txt-apoio text-tinta-apagada">
                A IA lê o documento de origem. Escolha o alvo e o documento. Cada tarefa sai com a página do manual, e só entra a que tem o intervalo escrito no trecho citado. O plano nasce em rascunho.
              </span>
            </div>
            {/* Enquanto a IA lê o manual (p.32), o lugar da resposta vira a forma
                dela: as tarefas, cada uma com o intervalo e a página. */}
            {extrair.isPending ? (
              <EsqueletoDeLista oQue="Lendo o manual e extraindo as tarefas" itens={3} icone={false} />
            ) : extracao ? (
              <p role="status" className="txt-apoio text-tinta-secundaria">{extracao}</p>
            ) : null}
          </div>
        ) : null}
      </section>

      {recusa === null ? null : (
        <div className="px-6 pb-6">
          <AvisoDeRecusa recusa={recusa} />
        </div>
      )}

      {/* Barra de ações fixa (R01, p.28): primário à direita, o de sair como
          terciário à esquerda dele (p.15). */}
      <div className="barra-de-acoes rounded-b-card px-6">
        <Botao nivel="terciario" type="button" onClick={descartar}>
          Descartar plano
        </Botao>
        <Botao nivel="primario" type="submit" disabled={criar.isPending}>
          Gravar rascunho
        </Botao>
      </div>
    </form>
  );
}
